import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { payComponentKeys, employeeKeys } from '@/lib/query-keys';
import { payComponentService, salaryStructureService } from '@/services/payroll.service';
import type { StructureLineInput } from '@/types/payroll';

function apiMessage(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || fallback;
}

export function usePayComponents(includeInactive = false) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: payComponentKeys.list(includeInactive),
    queryFn: () => payComponentService.getAll(includeInactive),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: payComponentKeys.all });
    // A catalog edit changes every derived breakdown, so drop those too.
    queryClient.invalidateQueries({ queryKey: employeeKeys.all });
  };

  const createComponent = useMutation({
    mutationFn: (data: Record<string, unknown>) => payComponentService.create(data),
    onSuccess: () => { invalidate(); toast.success('Pay component created'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to create pay component')),
  });

  const updateComponent = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => payComponentService.update(id, data),
    onSuccess: () => { invalidate(); toast.success('Pay component updated'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to update pay component')),
  });

  const deleteComponent = useMutation({
    mutationFn: (id: string) => payComponentService.delete(id),
    onSuccess: () => { invalidate(); toast.success('Pay component deleted'); },
    onError: (e) => toast.error(apiMessage(e, 'Failed to delete pay component')),
  });

  return {
    components: query.data?.data ?? [],
    isLoading: query.isLoading,
    createComponent,
    updateComponent,
    deleteComponent,
  };
}

/**
 * Live breakdown for the salary editor. Kept as a mutation rather than a query
 * so the caller controls exactly when it recomputes (debounced on edit).
 */
export function useSalaryPreview() {
  const preview = useMutation({
    mutationFn: (data: { ctcAnnual: number; lines: StructureLineInput[] }) => payComponentService.preview(data),
  });

  return {
    preview,
    breakdown: preview.data?.data,
    // The calculator's rejection message is the useful part of the error here.
    error: preview.isError ? apiMessage(preview.error, 'Could not compute the breakdown') : null,
    isComputing: preview.isPending,
  };
}

export function useSalaryStructureBreakdown(structureId?: string) {
  const query = useQuery({
    queryKey: structureId ? ['salary-structures', 'detail', structureId, 'breakdown'] : ['noop'],
    queryFn: () => salaryStructureService.getBreakdown(structureId as string),
    enabled: !!structureId,
  });
  return { breakdown: query.data?.data, isLoading: query.isLoading };
}
